/**
 * ATS onboarding API client.
 */

import { createApiClient } from '@aaas/shared';
import type {
  OnboardingProgress,
  OnboardingProgressResponse,
  OnboardingStepKey,
  SeedBusinessDataResponse,
  SeedBusinessDataResult,
} from '../types/onboarding';

class OnboardingService {
  async getProgress(workspaceId: string, idToken: string): Promise<OnboardingProgress> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<OnboardingProgressResponse>('/onboarding', {
      headers: { 'X-Workspace-ID': workspaceId },
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to load onboarding progress');
    }
    return response.data.data;
  }

  async dismissDashboard(
    workspaceId: string,
    idToken: string,
  ): Promise<OnboardingProgress> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<OnboardingProgressResponse>(
      '/onboarding/dismiss-dashboard',
      {},
      { headers: { 'X-Workspace-ID': workspaceId } },
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to dismiss onboarding');
    }
    return response.data.data;
  }

  async dismissStep(
    workspaceId: string,
    step: OnboardingStepKey,
    idToken: string,
  ): Promise<OnboardingProgress> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<OnboardingProgressResponse>(
      `/onboarding/steps/${step}/dismiss`,
      {},
      { headers: { 'X-Workspace-ID': workspaceId } },
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to dismiss step');
    }
    return response.data.data;
  }

  /**
   * Trigger backend seed of business demo data (Position/Campaign/Job/CV).
   * Caller MUST have seeded reference data first (Source / EmployeeType / ...
   * / Pipeline) via the per-resource bulk endpoints; otherwise the backend
   * will respond 400 "Missing reference data".
   */
  async seedBusinessData(
    workspaceId: string,
    idToken: string,
  ): Promise<SeedBusinessDataResult> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<SeedBusinessDataResponse>(
      '/onboarding/sample-data',
      {},
      { headers: { 'X-Workspace-ID': workspaceId } },
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to seed business data');
    }
    return response.data.data;
  }
}

const onboardingService = new OnboardingService();
export default onboardingService;
